import React from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TLocalLicenseSelectProps } from '@/console/features/licenses/components/local-license-select/local-license-select.types';
import { LocalSearchableSelect } from '@/shared/components/ui/form/components/searchable-select/variant/local/local-searchable-select';
import { useVendor } from '@/shared/hooks/use-vendor/use-vendor';
import { useReadAllLicensesQuery } from '@/shared/redux/api/assets/licenses/licenses-api';

export const LocalLicenseSelect = <TFieldValues extends FieldValues>({
  name,
  vendorId,
  ideConfigId,
  useProductAsValue,
}: TLocalLicenseSelectProps<TFieldValues>) => {
  const { t } = useTranslation();
  const { data, isLoading } = useReadAllLicensesQuery();
  const { getIDE } = useVendor();
  const ide = getIDE(vendorId, ideConfigId);
  const allowedLicenseVersions = ide?.allowedLicenseVersions ?? [];

  const getOptions = () => {
    if (!data) {
      return [];
    }

    const licenses = data.readAllLicenses.items;
    const uniqueLicenseOptions = new Set();

    return licenses
      .filter(
        (license) =>
          license.ideConfigId &&
          allowedLicenseVersions?.includes(license.ideConfigId),
      )
      .map((license) => {
        const getAdditionalInfo = () => {
          if (useProductAsValue) {
            return undefined;
          }

          return license.status
            ? t(`licenses:license_status_enum.${license.status.toLowerCase()}`)
            : t('licenses:unknown');
        };

        return {
          label: license.product,
          value: useProductAsValue ? license.product : license.licenseId,
          additionalInfo: getAdditionalInfo(),
        };
      })
      .filter((option) => {
        if (uniqueLicenseOptions.has(option.value)) {
          return false;
        }
        uniqueLicenseOptions.add(option.value);
        return true;
      });
  };

  const options = getOptions();

  if (!allowedLicenseVersions?.length) {
    return null;
  }

  return (
    <LocalSearchableSelect<TFieldValues>
      name={name}
      disabled={isLoading}
      label={t('common:license')}
      options={options}
      multiple
      getItemId={(value) => value}
      renderNoOptions={t('licenses:no_license_available')}
    />
  );
};
